import { useState, type FormEvent } from 'react';
import { api, type CreateSessionResponse } from '../api/client';

/**
 * Normalize common phone formats to E.164.
 * - Strips all non-digit characters except a leading `+`
 * - 10 digits (no +) → prepend +1 (US default)
 * - 11 digits starting with 1 (no +) → prepend +
 * - Already has + → keep as-is after stripping punctuation
 */
function normalizePhone(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');

  if (hasPlus) {
    return '+' + digits;
  }

  if (digits.length === 10) {
    return '+1' + digits;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return '+' + digits;
  }

  // Fallback: prepend + and let validation catch invalid lengths
  return '+' + digits;
}

interface Props {
  onCreated: (result: CreateSessionResponse) => void;
}

export default function CreateSessionForm({ onCreated }: Props) {
  const [candidateName, setCandidateName] = useState('');
  const [recruiterPhone, setRecruiterPhone] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const phone = normalizePhone(recruiterPhone);
    if (phone && !/^\+[1-9]\d{1,14}$/.test(phone)) {
      setError('Enter a valid phone number (e.g. (415) 555-1234)');
      return;
    }

    setLoading(true);
    try {
      const result = await api.createSession({
        candidateName: candidateName.trim(),
        recruiterPhone: phone || undefined,
        candidateEmail: candidateEmail.trim() || undefined,
      });
      onCreated(result);
      setCandidateName('');
      setRecruiterPhone('');
      setCandidateEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <h2>New Liveness Check</h2>
      <div className="create-grid">
        <div className="field">
          <label className="field-label" htmlFor="candidateName">
            Candidate Name
          </label>
          <input
            id="candidateName"
            className="input"
            type="text"
            required
            placeholder="Jane Doe"
            value={candidateName}
            onChange={(e) => setCandidateName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="recruiterPhone">
            Phone <span className="field-hint">(optional, for SMS)</span>
          </label>
          <input
            id="recruiterPhone"
            className="input"
            type="tel"
            placeholder="(555) 123-4567"
            value={recruiterPhone}
            onChange={(e) => setRecruiterPhone(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="candidateEmail">
            Candidate Email <span className="field-hint">(optional, sends link)</span>
          </label>
          <input
            id="candidateEmail"
            className="input"
            type="email"
            placeholder="jane@example.com"
            value={candidateEmail}
            onChange={(e) => setCandidateEmail(e.target.value)}
          />
        </div>
      </div>
      {error && (
        <p className="error-msg" style={{ marginBottom: 'var(--space-4)' }}>
          {error}
        </p>
      )}
      <div className="create-actions">
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Check Link'}
        </button>
      </div>
    </form>
  );
}
